import React, { useState } from 'react';
import { Brand, Character, Platform } from '../types';
import { ICONS, HEBREW_FONTS } from '../constants.tsx';
// @ts-ignore
import heic2any from 'heic2any';
import { reportError } from '../services/errorReporting';

interface Props {
  brands: Brand[];
  mode?: 'select' | 'manage';
  onSave: (brand: Brand) => void;
  onSelect: (brand: Brand) => void;
  onCancel: () => void;
  saving?: boolean;
}

const MAX_IMAGES_PER_CHARACTER = 2; // Updated from 3 to 2
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const BrandManager: React.FC<Props> = ({ brands, mode = 'manage', onSave, onSelect, onCancel, saving = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [dragActiveId, setDragActiveId] = useState<string | null>(null);
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({}); // Track loading state per character

  const [currentBrand, setCurrentBrand] = useState<Partial<Brand>>({
    name: '',
    characters: [],
    niche: '',
    targetAudience: '',
    businessDescription: '',
    preferredColors: '',
    preferredFont: 'Rubik',
    preferredPlatform: Platform.YOUTUBE
  });

  const handleCardClick = (brand: Brand) => {
    if (mode === 'select') {
        onSelect(brand);
    } else {
        handleEditClick(brand);
    }
  };

  const handleEditClick = (brand: Brand) => {
    setCurrentBrand({...brand});
    setIsEditing(true);
  };

  // Helper: Compress and normalize image
  const compressImage = (file: File | Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_DIMENSION = 1024; // Limit max dimension to save space
            let width = img.width;
            let height = img.height;

            if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                if (width > height) {
                    height *= MAX_DIMENSION / width;
                    width = MAX_DIMENSION;
                } else {
                    width *= MAX_DIMENSION / height;
                    height = MAX_DIMENSION;
                }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                URL.revokeObjectURL(url);
                reject(new Error("Could not get canvas context"));
                return;
            }

            // Draw white background for transparent images (optional, but good for standardization)
            ctx.fillStyle = '#FFFFFF'; 
            // Draw image
            ctx.drawImage(img, 0, 0, width, height);

            // Compress to JPEG with 0.8 quality
            const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            URL.revokeObjectURL(url);
            resolve(dataUrl);
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Failed to load image for compression"));
        };

        img.src = url;
    });
  };

  // Helper to process files from Input or Drop
  const processFiles = async (files: File[], charId: string) => {
    if (files.length === 0) return;

    const currentImages = currentBrand.characters?.find(c => c.id === charId)?.images || [];
    const remainingSlots = MAX_IMAGES_PER_CHARACTER - currentImages.length;

    if (remainingSlots <= 0) {
        alert(`הגעת למקסימום של ${MAX_IMAGES_PER_CHARACTER} תמונות לדמות`);
        return;
    }

    if (files.length > remainingSlots) {
        alert(`ניתן להוסיף רק עוד ${remainingSlots} תמונות. נוסיף את הראשונות.`);
    }

    setLoadingImages(prev => ({ ...prev, [charId]: true }));

    const filesToProcess = files.slice(0, remainingSlots);
    const newImages: string[] = [];

    for (let file of filesToProcess) {
        // Size Validation
        if (file.size > MAX_FILE_SIZE_BYTES) {
            alert(`הקובץ ${file.name} גדול מדי (מעל ${MAX_FILE_SIZE_MB}MB) ודולג.`);
            continue;
        }

        try {
            let fileToCompress: File | Blob = file;

            // HEIC Conversion Logic
            if (file.name.toLowerCase().endsWith('.heic') || file.type === 'image/heic') {
                try {
                    const resultBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 });
                    // heic2any can return an array or single blob
                    fileToCompress = Array.isArray(resultBlob) ? resultBlob[0] : resultBlob;
                } catch (heicError) {
                    reportError(heicError, { module: 'brand', extra: { action: 'heic-conversion', fileName: file.name } });
                    alert(`שגיאה בהמרת קובץ HEIC: ${file.name}`);
                    continue;
                }
            } else if (!file.type.startsWith('image/')) {
                alert(`הקובץ ${file.name} אינו תמונה.`);
                continue;
            }

            // Compression & Base64 Conversion
            const base64 = await compressImage(fileToCompress);
            newImages.push(base64);

        } catch (error) {
            reportError(error, { module: 'brand', extra: { action: 'process-file', fileName: file.name } });
            alert(`שגיאה בעיבוד הקובץ ${file.name}`);
        }
    }

    if (newImages.length > 0) {
        setCurrentBrand(prev => ({
            ...prev,
            characters: prev.characters?.map(c => 
                c.id === charId ? { ...c, images: [...c.images, ...newImages] } : c
            )
        }));
    }
    
    setLoadingImages(prev => ({ ...prev, [charId]: false }));
  };

  const handleCharacterImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, charId: string) => {
    const files: File[] = Array.from(e.target.files || []);
    await processFiles(files, charId);
    e.target.value = ''; // Reset input
  };

  const handleDragEnter = (e: React.DragEvent, charId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActiveId(charId);
  };

  const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Needed for drop to work
  };

  const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Prevent flicker when hovering over children
      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
      
      setDragActiveId(null);
  };

  const handleDrop = async (e: React.DragEvent, charId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActiveId(null);
      
      const files = Array.from(e.dataTransfer.files) as File[];
      if (files && files.length > 0) {
          await processFiles(files, charId);
      }
  };

  const removeCharacterImage = (charId: string, imgIndex: number) => {
    setCurrentBrand(prev => ({
        ...prev,
        characters: prev.characters?.map(c => 
            c.id === charId ? { ...c, images: c.images.filter((_, idx) => idx !== imgIndex) } : c
        )
    }));
  };

  const addNewCharacter = () => {
    const newChar: Character = {
      id: Date.now().toString(),
      name: 'שם הדמות',
      images: []
    };
    setCurrentBrand(prev => ({ ...prev, characters: [...(prev.characters || []), newChar] }));
  };

  const removeCharacter = (id: string) => {
    setCurrentBrand(prev => ({ ...prev, characters: prev.characters?.filter(c => c.id !== id) }));
  };

  const updateCharacterName = (id: string, name: string) => {
    setCurrentBrand(prev => ({
        ...prev,
        characters: prev.characters?.map(c => c.id === id ? { ...c, name } : c)
    }));
  };

  const handleSave = () => {
    if (!currentBrand.name || !currentBrand.niche) {
        alert("נא למלא שדות חובה (שם מותג ונישה)");
        return;
    }
    onSave({
        id: currentBrand.id || Date.now().toString(),
        name: currentBrand.name!,
        businessDescription: currentBrand.businessDescription || '',
        niche: currentBrand.niche!,
        targetAudience: currentBrand.targetAudience || '',
        characters: currentBrand.characters || [],
        preferredColors: currentBrand.preferredColors ?? '',
        preferredFont: currentBrand.preferredFont ?? 'Rubik',
        preferredPlatform: currentBrand.preferredPlatform || Platform.YOUTUBE
    });
    setIsEditing(false);
  };

  if (isEditing || brands.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-4 sm:p-6 md:p-8 bg-click-card border border-click-border rounded-xl shadow-2xl break-words">
        <h2 className="text-3xl font-display font-bold text-click-red mb-6 flex items-center gap-2">
          <ICONS.Palette className="w-8 h-8" />
          {currentBrand.id ? 'עריכת מותג' : 'יצירת מותג חדש'}
        </h2>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-click-cyan font-bold mb-2">שם המותג / ערוץ</label>
              <input 
                type="text" 
                className="w-full bg-click-dark border border-click-border focus:border-click-red text-white p-3 rounded"
                value={currentBrand.name}
                onChange={e => setCurrentBrand({...currentBrand, name: e.target.value})}
                placeholder="למשל: הערוץ של יוסי"
              />
            </div>
            <div>
              <label className="block text-click-cyan font-bold mb-2">נישה</label>
              <input 
                type="text" 
                className="w-full bg-click-dark border border-click-border focus:border-click-red text-white p-3 rounded"
                value={currentBrand.niche}
                onChange={e => setCurrentBrand({...currentBrand, niche: e.target.value})}
                placeholder="למשל: גיימינג, פיננסים, בישול"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <label className="block text-click-cyan font-bold mb-2">קהל יעד</label>
                <input 
                    type="text" 
                    className="w-full bg-click-dark border border-click-border focus:border-click-red text-white p-3 rounded"
                    value={currentBrand.targetAudience}
                    onChange={e => setCurrentBrand({...currentBrand, targetAudience: e.target.value})}
                    placeholder="גילאים, תחומי עניין..."
                />
            </div>
            {/* Preferred Platform Selection */}
            <div>
                <label className="block text-click-cyan font-bold mb-2">פלטפורמה מועדפת (ברירת מחדל)</label>
                <div className="flex bg-click-dark rounded-lg p-1 border border-click-border">
                    {[Platform.YOUTUBE, Platform.INSTAGRAM].map(platform => (
                        <button
                            key={platform}
                            onClick={() => setCurrentBrand({...currentBrand, preferredPlatform: platform})}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded text-sm transition-all font-bold ${
                                currentBrand.preferredPlatform === platform 
                                ? 'bg-click-border text-click-red shadow-sm' 
                                : 'text-gray-500 hover:text-white'
                            }`}
                        >
                            {platform === Platform.YOUTUBE ? <ICONS.Youtube className="w-4 h-4" /> : <ICONS.Instagram className="w-4 h-4" />}
                            {platform}
                        </button>
                    ))}
                </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div>
                <label className="block text-click-cyan font-bold mb-2">פונט מועדף</label>
                <select 
                    className="w-full bg-click-dark border border-click-border focus:border-click-red text-white p-3 rounded"
                    value={currentBrand.preferredFont}
                    onChange={e => setCurrentBrand({...currentBrand, preferredFont: e.target.value})}
                    style={{ fontFamily: HEBREW_FONTS.find(f => f.value === currentBrand.preferredFont)?.family }}
                >
                    {HEBREW_FONTS.map(font => (
                        <option key={font.value} value={font.value} style={{ fontFamily: font.family }}>
                            {font.name}
                        </option>
                    ))}
                </select>
            </div>
           <div>
                <label className="block text-click-cyan font-bold mb-2">צבעוניות מועדפת</label>
                <input 
                    type="text" 
                    className="w-full bg-click-dark border border-click-border focus:border-click-red text-white p-3 rounded"
                    value={currentBrand.preferredColors}
                    onChange={e => setCurrentBrand({...currentBrand, preferredColors: e.target.value})}
                    placeholder="למשל: צהוב ושחור, צבעי פסטל..."
                />
            </div>
          </div>

          <div>
             <label className="block text-click-cyan font-bold mb-2">תיאור העסק</label>
             <textarea 
                className="w-full bg-click-dark border border-click-border focus:border-click-red text-white p-3 rounded h-24"
                value={currentBrand.businessDescription}
                onChange={e => setCurrentBrand({...currentBrand, businessDescription: e.target.value})}
                placeholder="ספרו בקצרה על העסק והסגנון של המותג – כדי שאוכל להתאים את הרעיונות והתמונות לסגנון ולתדמית שלכם"
              />
          </div>

          {/* Characters Section */}
          <div className="border-t border-click-border pt-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white">דמויות (עד 2)</h3>
                {(currentBrand.characters?.length || 0) < 2 && (
                    <button onClick={addNewCharacter} className="text-click-red hover:text-white flex items-center gap-1">
                        <ICONS.Plus className="w-4 h-4" /> הוסף דמות
                    </button>
                )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {currentBrand.characters?.map((char) => (
                    <div key={char.id} className="bg-click-dark p-4 rounded border border-click-border relative group">
                        <button onClick={() => removeCharacter(char.id)} className="absolute top-2 left-2 text-click-red opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <ICONS.Trash2 className="w-5 h-5" />
                        </button>
                        <div className="mb-3">
                            <input 
                                type="text"
                                value={char.name}
                                onChange={(e) => updateCharacterName(char.id, e.target.value)}
                                className="bg-transparent border-b border-click-border focus:border-click-red text-white w-full font-bold"
                                placeholder="שם הדמות"
                            />
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2 mb-3">
                             {char.images.map((img, idx) => (
                                 <div key={idx} className="relative aspect-square rounded overflow-hidden border border-click-border group/img">
                                     <img src={img} className="w-full h-full object-cover" />
                                     <button 
                                        onClick={() => removeCharacterImage(char.id, idx)}
                                        className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover/img:opacity-100 text-red-500"
                                     >
                                         <ICONS.Trash2 className="w-4 h-4" />
                                     </button>
                                 </div>
                             ))}
                             {char.images.length < MAX_IMAGES_PER_CHARACTER && (
                                 <label 
                                    className={`aspect-square border-2 border-dashed flex flex-col items-center justify-center rounded cursor-pointer transition-all duration-200 relative overflow-hidden
                                    ${dragActiveId === char.id 
                                        ? 'border-click-red bg-click-red/10 text-click-red scale-105 shadow-[0_0_20px_rgba(255,0,60,0.2)]' 
                                        : 'border-click-border hover:border-click-red text-gray-500 hover:text-click-red'
                                    }`}
                                    onDragEnter={(e) => handleDragEnter(e, char.id)}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, char.id)}
                                 >
                                    {loadingImages[char.id] ? (
                                        <div className="absolute inset-0 flex items-center justify-center bg-click-card z-10">
                                            <ICONS.Loader2 className="w-6 h-6 animate-spin text-click-cyan" />
                                        </div>
                                    ) : (
                                        dragActiveId === char.id ? (
                                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-click-card/90 z-10 animate-pulse">
                                                <ICONS.Download className="w-8 h-8 mb-2 animate-bounce" />
                                                <span className="text-xs font-bold uppercase tracking-widest">Drop Image</span>
                                            </div>
                                        ) : (
                                            <>
                                                <ICONS.Plus className="w-6 h-6 mb-1" />
                                                <span className="text-caption font-bold">גרור או לחץ</span>
                                                <span className="text-caption text-gray-500 mt-1">עד {MAX_FILE_SIZE_MB}MB</span>
                                            </>
                                        )
                                    )}
                                     
                                     <input 
                                        type="file" 
                                        accept="image/png, image/jpeg, image/webp, image/heic, .heic"
                                        className="hidden"
                                        multiple
                                        onChange={(e) => handleCharacterImageUpload(e, char.id)}
                                     />
                                 </label>
                             )}
                        </div>
                        
                        {/* Guidelines Checklist */}
                        <div className="bg-click-card/50 border border-click-border/50 rounded p-2 text-caption text-gray-400">
                             <span className="text-click-cyan font-bold block mb-1">🧪 הנוסחה לזיהוי מושלם:</span>
                             <ul className="space-y-1">
                                <li className="flex items-center gap-1.5 text-gray-300">
                                    <ICONS.Check className="w-3 h-3 text-green-500" />
                                    <span>תמונות פנים ברורות וקרובות</span>
                                </li>
                                <li className="flex items-center gap-1.5 text-gray-300">
                                    <ICONS.Check className="w-3 h-3 text-green-500" />
                                    <span>תאורה טובה, ללא צללים כבדים</span>
                                </li>
                                <li className="flex items-center gap-1.5 text-gray-400/70">
                                    <ICONS.X className="w-3 h-3 text-click-red" />
                                    <span>ללא משקפי שמש או הסתרות</span>
                                </li>
                                <li className="flex items-center gap-1.5 text-gray-400/70">
                                    <ICONS.X className="w-3 h-3 text-click-red" />
                                    <span>ללא תמונות קבוצתיות</span>
                                </li>
                             </ul>
                        </div>

                        <div className="text-xs text-gray-500 text-center mt-2">
                            {char.images.length}/{MAX_IMAGES_PER_CHARACTER} תמונות
                        </div>
                    </div>
                ))}
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-8">
            {brands.length > 0 && <button onClick={() => setIsEditing(false)} disabled={saving} className="text-white hover:text-click-red font-bold disabled:opacity-50">ביטול</button>}
            <button onClick={handleSave} disabled={saving} className="bg-click-cyan text-black px-8 py-3 rounded font-bold font-display hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {saving ? <><ICONS.Loader2 className="w-4 h-4 animate-spin" /> שומר...</> : 'שמור מותג'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {brands.map(brand => (
        <div 
            key={brand.id} 
            className="bg-click-card p-6 rounded-xl border border-click-border hover:border-click-red transition-colors cursor-pointer group relative" 
            onClick={() => handleCardClick(brand)}
        >
            <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-display font-bold text-white group-hover:text-click-red">{brand.name}</h3>
                <div className="flex gap-2">
                    {mode === 'select' ? (
                        <div className="bg-click-cyan/20 p-2 rounded-full text-click-cyan">
                            <ICONS.ArrowRight className="w-4 h-4" />
                        </div>
                    ) : (
                         <div className="bg-click-border hover:bg-click-red hover:text-white transition-colors p-2 rounded-full text-white">
                            <ICONS.Edit2 className="w-4 h-4" />
                        </div>
                    )}
                </div>
            </div>
            <p className="text-gray-400 mb-2">{brand.niche}</p>
            
            {/* Preferred Platform Badge */}
            <div className="mb-4">
                 <span className="inline-flex items-center gap-1 text-caption bg-click-dark border border-click-border px-2 py-0.5 rounded text-gray-400">
                    {brand.preferredPlatform === Platform.INSTAGRAM ? <ICONS.Instagram className="w-3 h-3" /> : <ICONS.Youtube className="w-3 h-3" />}
                    {brand.preferredPlatform || Platform.YOUTUBE}
                 </span>
            </div>

            <div className="flex flex-wrap gap-2">
                {brand.characters.map(c => (
                    c.images.length > 0 && (
                        <div key={c.id} className="w-10 h-10 rounded-full overflow-hidden border border-click-border relative group/tooltip">
                             <img src={c.images[0]} className="w-full h-full object-cover" />
                             <div className="absolute bottom-full left-1/2 -translate-x-1/2 bg-black text-xs text-white px-2 py-1 rounded opacity-0 group-hover/tooltip:opacity-100 whitespace-nowrap pointer-events-none">
                                 {c.name}
                             </div>
                        </div>
                    )
                ))}
            </div>

            {/* If in select mode, allow explicit edit via small button */}
            {mode === 'select' && (
                <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(brand);
                    }}
                    className="absolute bottom-4 left-4 p-2 text-gray-500 hover:text-white"
                    title="ערוך מותג"
                >
                    <ICONS.Edit2 className="w-4 h-4" />
                </button>
            )}
        </div>
      ))}
      <div 
        onClick={() => {
            setCurrentBrand({ 
                characters: [], 
                name: '', 
                niche: '', 
                targetAudience: '', 
                businessDescription: '', 
                preferredFont: 'Rubik',
                preferredPlatform: Platform.YOUTUBE 
            });
            setIsEditing(true);
        }}
        className="border-2 border-dashed border-click-border rounded-xl flex flex-col items-center justify-center p-6 cursor-pointer hover:border-click-cyan hover:bg-click-cyan/5 transition-all min-h-[200px]"
      >
        <ICONS.Plus className="w-12 h-12 text-click-cyan mb-4" />
        <span className="text-click-cyan font-bold text-lg">צור מותג חדש</span>
      </div>
    </div>
  );
};

export default BrandManager;